import React, { useMemo } from "react";
import {
  Card,
  Table,
  Alert,
  Space,
  Typography,
  Tooltip as AntTooltip,
} from "antd";
import { TrophyOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getHeatmapColors } from "../utils/heatmapColors";
import HeatmapLegend from "./HeatmapLegend";

const { Text } = Typography;

const CompleteOverviewTab = ({
  comprehensiveMatrixData,
  heatmapScale,
  allTradingDays,
  expectedMove,
  calculateBestOptions,
  selectedStrike,
  setSelectedStrike,
  multiStrikeTimeDecayData,
  strikes,
  strikeComparisonData,
  bestOption,
  underlyingPrice,
}) => {
  const atmStrike = useMemo(() => {
    if (!underlyingPrice || !strikes?.length) return null;
    return strikes.reduce((closest, s) =>
      Math.abs(s.strike - underlyingPrice) <
      Math.abs(closest.strike - underlyingPrice)
        ? s
        : closest,
    ).strike;
  }, [strikes, underlyingPrice]);

  return (
    <>
      <Alert
        message="📊 Dynamic Heatmap - Colors Scale to Your Actual Data"
        description={
          <div>
            <p>
              <strong>Find Your Perfect Strike:</strong> This heatmap shows ALL
              strikes across ALL time periods for your {expectedMove}% target
              move. <strong>Colors dynamically scale</strong> from your worst
              case ({heatmapScale.minNegative.toFixed(0)}%) to your best case (
              {heatmapScale.maxPositive.toFixed(0)}%):{" "}
              <strong style={{ color: "#00ff41" }}>Neon Green</strong> = Top 10%
              performers (extreme returns),{" "}
              <strong style={{ color: "#52c41a" }}>Bright Green</strong> = Top
              25-50%, <strong style={{ color: "#95de64" }}>Light Green</strong>{" "}
              = Mid-range profits,{" "}
              <strong style={{ color: "#d4b106" }}>Dim Yellow</strong> = Small
              gains, <strong style={{ color: "#ff4d4f" }}>Any Red</strong> =
              Zero or negative returns (avoid!).
            </p>
            <p>
              <strong>Goal:</strong> Look for strikes with the brightest colors
              that stay consistent across multiple days. The brighter the green,
              the better it performs relative to all options!
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card
        title={
          <Space>
            <Text strong>
              🔥 Profit/Loss Heatmap: All Strikes × All Time Periods (
              {expectedMove}% move)
            </Text>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={comprehensiveMatrixData}
          pagination={false}
          rowKey="strike"
          size="small"
          scroll={{ x: 1500 }}
          columns={[
            {
              title: "Strike",
              dataIndex: "strike",
              key: "strike",
              fixed: "left",
              width: 100,
              render: (val, record) => {
                const isBest =
                  calculateBestOptions &&
                  calculateBestOptions[0]?.strike === val;
                const isSelected = val === selectedStrike;
                const isATM = val === atmStrike;
                return (
                  <div>
                    <Space>
                      <Text
                        strong={isSelected || isBest}
                        style={{
                          color: isBest ? "#faad14" : undefined,
                        }}
                      >
                        ${val}
                      </Text>
                      {isBest && (
                        <TrophyOutlined style={{ color: "#faad14" }} />
                      )}
                      {isATM && (
                        <AntTooltip title="Closest strike to current underlying price">
                          <Text
                            style={{
                              fontSize: 10,
                              color: "#1890ff",
                              border: "1px solid #1890ff",
                              borderRadius: 3,
                              padding: "0 4px",
                            }}
                          >
                            ATM
                          </Text>
                        </AntTooltip>
                      )}
                    </Space>
                    {record.iv != null && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          IV {(record.iv * 100).toFixed(1)}%
                        </Text>
                      </>
                    )}
                  </div>
                );
              },
            },
            ...allTradingDays.map((day) => ({
              title: `Day ${day}`,
              dataIndex: `day_${day}`,
              key: `day_${day}`,
              width: 110,
              render: (data, record) => {
                if (!data) return <Text type="secondary">-</Text>;

                const { bgColor, textColor, borderColor } = getHeatmapColors(
                  data.returnPct,
                  heatmapScale,
                );

                return (
                  <AntTooltip
                    title={
                      <div>
                        <div>
                          <strong>Entry Cost (model):</strong> $
                          {data.entryPrice.toFixed(2)}
                        </div>
                        {record.bid != null && record.ask != null && (
                          <div>
                            <strong>Live Bid/Ask:</strong> $
                            {record.bid.toFixed(2)} / ${record.ask.toFixed(2)}
                          </div>
                        )}
                        <div>
                          <strong>Exit Price:</strong> $
                          {data.exitPrice.toFixed(2)}
                        </div>
                      </div>
                    }
                    placement="top"
                  >
                    <div
                      style={{
                        background: bgColor,
                        padding: "4px 8px",
                        borderRadius: 4,
                        textAlign: "center",
                        border: `1px solid ${borderColor}`,
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <Text
                        strong
                        style={{
                          color: textColor,
                          fontSize: 13,
                        }}
                      >
                        {data.returnPct > 0 ? "+" : ""}
                        {data.returnPct.toFixed(1)}%
                      </Text>
                      <br />
                      <Text
                        style={{
                          fontSize: 11,
                          color: textColor,
                        }}
                      >
                        {data.profit > 0 ? "+" : ""}${data.profit.toFixed(2)}
                      </Text>
                    </div>
                  </AntTooltip>
                );
              },
            })),
          ]}
          onRow={(record) => ({
            onClick: () => setSelectedStrike(record.strike),
            style: {
              cursor: "pointer",
              background:
                record.strike === selectedStrike ? "#1a3a52" : undefined,
            },
          })}
        />
        <HeatmapLegend heatmapScale={heatmapScale} />
      </Card>

      <Card
        title="📈 All Strikes Performance Over Time"
        style={{ marginBottom: 24 }}
      >
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={multiStrikeTimeDecayData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              label={{
                value: "Trading Days",
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              label={{
                value: "Return %",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip formatter={(value) => `${value?.toFixed(2)}%`} />
            <Legend />
            {strikes.map((strikeData, index) => {
              const colors = [
                "#1890ff",
                "#52c41a",
                "#faad14",
                "#ff4d4f",
                "#722ed1",
                "#13c2c2",
                "#eb2f96",
                "#fa8c16",
              ];
              const isSelected = strikeData.strike === selectedStrike;
              const isBest =
                calculateBestOptions &&
                calculateBestOptions[0]?.strike === strikeData.strike;

              return (
                <Line
                  key={strikeData.strike}
                  type="monotone"
                  dataKey={`Strike $${strikeData.strike}`}
                  stroke={isBest ? "#faad14" : colors[index % colors.length]}
                  strokeWidth={isSelected || isBest ? 3 : 2}
                  dot={{ r: isSelected || isBest ? 6 : 3 }}
                  name={`$${strikeData.strike}${isBest ? " 🏆" : ""}`}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
        <Alert
          message="How to Read This Chart"
          description={
            <ul>
              <li>
                Each line represents a different strike price for your{" "}
                {expectedMove}% target move
              </li>
              <li>
                Lines above 0% = Profitable | Lines below 0% = Losing money
              </li>
              <li>
                <strong>Best strike (🏆)</strong> is highlighted in gold with
                thicker line
              </li>
              <li>
                Look for strikes that stay positive (above 0%) across multiple
                days
              </li>
              <li>
                Steeper downward slopes = faster time decay (more risk if timing
                is wrong)
              </li>
            </ul>
          }
          type="info"
          showIcon
        />
      </Card>

      <Card title={`💰 Profit Comparison on Day ${allTradingDays[0]}`}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={strikeComparisonData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="strike" tickFormatter={(val) => `$${val}`} />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Profit") return `$${value.toFixed(2)}`;
                return value;
              }}
            />
            <Legend />
            <Bar dataKey="priceChange" fill="#1890ff" name="Profit">
              {strikeComparisonData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.priceChange < 0
                      ? "#ff4d4f"
                      : entry.strike === bestOption?.strike
                        ? "#faad14"
                        : "#52c41a"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="📊 Strike Comparison - Return %" style={{ marginTop: 24 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={strikeComparisonData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="strike" tickFormatter={(val) => `$${val}`} />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Return %") return `${value.toFixed(2)}%`;
                return value;
              }}
            />
            <Legend />
            <Bar dataKey="returnPct" fill="#1890ff" name="Return %">
              {strikeComparisonData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.strike === bestOption?.strike ? "#faad14" : "#1890ff"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
};

export default CompleteOverviewTab;
